import { useEffect, useMemo, useState } from "react";
import { getCached, setCached } from "@/lib/pageStateCache";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csvDownload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Filter, RefreshCw, Inbox, Eye, ReceiptText, Search, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import AppointmentDetails from "@/components/AppointmentDetails";
import SmsQuickSendDialog from "@/components/SmsQuickSendDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DateRangeFilter, { presetRange, ymd } from "@/components/DateRangeFilter";
import FilterPresetsBar from "@/components/FilterPresetsBar";
import { fmtDateTime } from "@/lib/intl";
import { useAuth } from "@/lib/auth";
import { MyFollowUpsCard } from "@/components/enquiries/MyFollowUps";

function ymdLocal(d) {
  return ymd(d);
}

const PAGE_SIZE = 25;

export default function Appointments() {
  const { status } = useOutletContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const staffFollowUps = user?.role === "staff" && <MyFollowUpsCard />;

  // Hydrate from in-memory cache so navigating away & back keeps filters + rows.
  const cached = getCached("appointments");

  const [preset, setPreset] = useState(cached?.preset ?? "this_month");
  const [range, setRange] = useState(() => cached?.range ?? presetRange("this_month"));
  const [minDate, maxDate] = range;

  const [appointmentTypeID, setAppointmentTypeID] = useState(cached?.appointmentTypeID ?? "all");
  const [calendarID, setCalendarID] = useState(cached?.calendarID ?? "all");
  const [includeCanceled, setIncludeCanceled] = useState(cached?.includeCanceled ?? "active");
  const [customerSearch, setCustomerSearch] = useState(cached?.customerSearch ?? "");

  const [types, setTypes] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [rows, setRows] = useState(cached?.rows ?? []);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(Boolean(cached?.rows?.length));
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(cached?.sort ?? { key: "datetime", dir: "desc" });
  const [page, setPage] = useState(cached?.page ?? 1);
  const [selected, setSelected] = useState(null);
  const [smsTarget, setSmsTarget] = useState(null);
  // sale_id keyed by Acuity appointment id (for "Already receipted" badge).
  const [receiptedById, setReceiptedById] = useState({});

  // Refresh receipt lookup whenever the rows change.
  useEffect(() => {
    if (!rows.length) { setReceiptedById({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const ids = rows.map((r) => String(r.id)).filter(Boolean).slice(0, 500);
        const r = await api.post("/receipts/by-source/bulk", {
          source: "appointment",
          source_ids: ids,
        }, { timeout: 20000 });
        if (!cancelled) setReceiptedById(r.data || {});
      } catch (e) {
        if (!cancelled) setReceiptedById({});
      }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  useEffect(() => {
    if (!status.configured) return;
    Promise.all([api.get("/appointment-types"), api.get("/calendars")])
      .then(([t, c]) => {
        setTypes(t.data || []);
        setCalendars(c.data || []);
      })
      .catch(() => {});
  }, [status.configured]);

  // When the user types a free-text client search (name or email) we
  // route it to the backend as ``customer_search`` so the server can
  // resolve the client via Acuity's /clients endpoint and fetch only
  // their appointments. This bypasses the generic 1000-row window pull
  // cap, which was previously dropping customers from busy periods
  // when they fell outside the first 1000 rows Acuity returned.
  function parseServerCustomerSearch(raw) {
    const q = (raw || "").trim();
    if (!q) return {};
    if (q.includes("@")) return { email: q };
    return { customer_search: q };
  }

  const queryParams = useMemo(
    () => ({
      minDate: ymd(minDate),
      maxDate: ymd(maxDate),
      appointmentTypeID: appointmentTypeID === "all" ? undefined : appointmentTypeID,
      calendarID: calendarID === "all" ? undefined : calendarID,
      canceled: includeCanceled === "canceled" ? true : undefined,
      max: 1000,
      ...parseServerCustomerSearch(customerSearch),
    }),
    [minDate, maxDate, appointmentTypeID, calendarID, includeCanceled, customerSearch]
  );

  async function fetchData() {
    if (!status.configured) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/appointments", { params: queryParams });
      setRows(r.data || []);
      setPage(1);
      setHasFetched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load appointments", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  // Persist filters + loaded rows into the in-memory cache so navigating away
  // (e.g., to /receipts) and back doesn't lose the user's loaded data set.
  useEffect(() => {
    setCached("appointments", {
      preset, range, appointmentTypeID, calendarID,
      includeCanceled, customerSearch, rows, sort, page,
      ts: Date.now(),
    });
  }, [preset, range, appointmentTypeID, calendarID, includeCanceled, customerSearch, rows, sort, page]);

  // Note: by design, we don't auto-fetch on mount — the user explicitly clicks
  // Apply (or changes any filter) so they don't pay for a large initial pull.

  // Client-side partial-match filter on the fetched rows. This runs on
  // top of the server-side date/type/calendar filters so users can
  // narrow further by typing any part of a first name, last name,
  // email, or phone number without triggering another Acuity fetch.
  const searchFiltered = useMemo(() => {
    const q = (customerSearch || "").trim().toLowerCase();
    if (!q || q.includes("@")) return rows; // email already server-filtered
    return rows.filter((r) => {
      const blob = [
        r.firstName, r.lastName,
        r.email,
        r.phone,
        `${r.firstName || ""} ${r.lastName || ""}`,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [rows, customerSearch]);

  const sorted = useMemo(() => {
    const data = [...searchFiltered];
    data.sort((a, b) => {
      const av = a?.[sort.key];
      const bv = b?.[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return data;
  }, [searchFiltered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function toggleSort(key) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  if (!status.configured) {
    return (
      <div className="space-y-6">
        {staffFollowUps}
        <NotConnected resource="appointments" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="appointments-page">
      {staffFollowUps}
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Bookings
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          Appointments
        </h1>
        <p className="mt-2 text-sm text-zinc-600">
          Filter by date range and dimension; export the result set as CSV.
        </p>
      </div>

      <Card className="p-5 sm:p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center gap-2 mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>

        <DateRangeFilter
          preset={preset}
          setPreset={setPreset}
          range={range}
          setRange={setRange}
          idPrefix="appts-range"
        />

        <div className="mt-4">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Customer name or email
          </Label>
          <div className="relative mt-1.5">
            <Input
              value={customerSearch}
              onChange={(e) => { setCustomerSearch(e.target.value); setPage(1); }}
              onKeyDown={(e) => { if (e.key === "Enter") fetchData(); }}
              placeholder="e.g. Derham · Shane · shane@example.com"
              className="border-zinc-300 pl-8"
              data-testid="appts-customer-search"
            />
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
            {customerSearch && (
              <button
                type="button"
                onClick={() => { setCustomerSearch(""); setPage(1); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900 text-xs"
                data-testid="appts-customer-search-clear"
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>
          <p className="mt-1 text-2xs text-zinc-500">
            Partial matches on first name, last name, phone or email — narrows the loaded rows
            instantly. For an email, we also send it to Acuity as a server-side filter.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Appointment type
            </Label>
            <Select value={appointmentTypeID} onValueChange={setAppointmentTypeID}>
              <SelectTrigger
                data-testid="filter-appointment-type"
                className="border-zinc-300"
              >
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Calendar</Label>
            <Select value={calendarID} onValueChange={setCalendarID}>
              <SelectTrigger data-testid="filter-calendar" className="border-zinc-300">
                <SelectValue placeholder="All calendars" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All calendars</SelectItem>
                {calendars.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Status</Label>
            <Select value={includeCanceled} onValueChange={setIncludeCanceled}>
              <SelectTrigger data-testid="filter-status" className="border-zinc-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            onClick={fetchData}
            data-testid="apply-filters-btn"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            Apply
          </Button>
          <FilterPresetsBar
            page="appointments"
            currentFilters={{
              preset, range, appointmentTypeID, calendarID,
              includeCanceled, customerSearch,
            }}
            onApply={(f) => {
              if (f?.preset) setPreset(f.preset);
              if (Array.isArray(f?.range) && f.range.length === 2) setRange(f.range);
              if (f?.appointmentTypeID !== undefined) setAppointmentTypeID(f.appointmentTypeID);
              if (f?.calendarID !== undefined) setCalendarID(f.calendarID);
              if (f?.includeCanceled !== undefined) setIncludeCanceled(f.includeCanceled);
              if (f?.customerSearch !== undefined) setCustomerSearch(f.customerSearch);
            }}
          />
          <Button
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={async () => {
              try {
                await downloadCsv(
                  "/appointments/csv",
                  queryParams,
                  `acuity_appointments_${ymd(minDate)}_${ymd(maxDate)}.csv`,
                );
              } catch (e) {
                toast.error("CSV download failed", { description: e.message });
              }
            }}
            data-testid="download-appointments-csv"
          >
            <Download className="h-4 w-4 mr-1.5" />
            Download CSV
          </Button>
          <span className="ml-auto text-xs text-zinc-500 font-mono-num" data-testid="row-count">
            {sorted.length} rows
          </span>
        </div>
      </Card>

      {error && (
        <div
          data-testid="appointments-error"
          className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                {[
                  ["datetime", "Date / time"],
                  ["firstName", "Client"],
                  ["email", "Email"],
                  ["type", "Type"],
                  ["calendar", "Calendar"],
                  ["price", "Price"],
                  ["amountPaid", "Paid"],
                  ["calculated_price", "Calc. price"],
                ].map(([key, label]) => (
                  <TableHead
                    key={key}
                    onClick={() => toggleSort(key)}
                    className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-zinc-600"
                    data-testid={`sort-${key}`}
                  >
                    {label}
                    {sort.key === key && (
                      <span className="ml-1 text-zinc-400">
                        {sort.dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </TableHead>
                ))}
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600 w-20">
                  Details
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={10} className="py-12 text-center text-sm text-zinc-500">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="py-16">
                    <EmptyRow
                      message={
                        hasFetched
                          ? "No appointments in this range"
                          : "Set your filters above and click Apply to load appointments."
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                paged.map((r) => (
                  <TableRow key={r.id} data-testid={`appt-row-${r.id}`}>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {r.datetime ? fmtDateTime(r.datetime) : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-zinc-950 font-medium flex items-center gap-1.5">
                        {[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}
                        <IntakeBadge intake={r.intake_form} />
                      </div>
                      <div className="text-xs text-zinc-500 font-mono-num">
                        #{r.id} {r.canceled ? "· canceled" : ""}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-700">{r.email || "—"}</TableCell>
                    <TableCell className="text-sm text-zinc-700">{r.type || "—"}</TableCell>
                    <TableCell className="text-sm text-zinc-700">{r.calendar || "—"}</TableCell>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {r.price ? `$${Number(r.price).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {r.amountPaid ? `$${Number(r.amountPaid).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell className="min-w-[140px]">
                      <CalcPriceCell row={r} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                          onClick={() => setSelected(r)}
                          data-testid={`view-appt-${r.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                          disabled={!r.phone}
                          onClick={() => setSmsTarget(r)}
                          data-testid={`sms-appt-${r.id}`}
                          title={r.phone ? "Send SMS to this client" : "No phone on file"}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                        </Button>
                        {receiptedById[String(r.id)] ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/receipts?regen=${encodeURIComponent(receiptedById[String(r.id)].sale_id)}`)}
                            className="inline-flex items-center gap-1.5 h-8 px-2 text-2xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 font-mono-num"
                            data-testid={`appt-receipted-${r.id}`}
                            title={`Sale ${receiptedById[String(r.id)].sale_id} · ${receiptedById[String(r.id)].generated_at?.slice(0, 10)}${receiptedById[String(r.id)].emailed_to ? ` · emailed to ${receiptedById[String(r.id)].emailed_to}` : ""}`}
                          >
                            <ReceiptText className="h-3.5 w-3.5 text-emerald-700" />
                            {receiptedById[String(r.id)].sale_id}
                          </button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                            onClick={() => navigate(`/receipts?source=appointment&id=${r.id}`)}
                            data-testid={`receipt-appt-${r.id}`}
                            title="Generate tax receipt for this appointment"
                          >
                            <ReceiptText className="h-3.5 w-3.5 mr-1" /> Receipt
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {sorted.length > PAGE_SIZE && (
          <div className="flex items-center justify-between p-4 border-t border-zinc-200">
            <span className="text-xs text-zinc-500 font-mono-num">
              Page {page} / {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev"
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="page-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <AppointmentDetails
        appointment={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />

      <SmsQuickSendDialog
        open={!!smsTarget}
        onClose={() => setSmsTarget(null)}
        title={smsTarget ? `SMS ${smsTarget.firstName || ""} ${smsTarget.lastName || ""}`.trim() : "Send SMS"}
        source="appointment_inline"
        recipients={smsTarget ? [{
          to: smsTarget.phone,
          raw_phone: smsTarget.phone,
          name: [smsTarget.firstName, smsTarget.lastName].filter(Boolean).join(" "),
          first_name: smsTarget.firstName,
          last_name: smsTarget.lastName,
          email: smsTarget.email,
          appointment_id: smsTarget.id,
          appointment_type: smsTarget.type,
          appointment_datetime: smsTarget.datetime,
          valid: !!smsTarget.phone,
        }] : []}
      />
    </div>
  );
}

function EmptyRow({ message }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-zinc-500">
      <Inbox className="h-7 w-7 mb-2 text-zinc-300" />
      <div className="text-sm">{message}</div>
    </div>
  );
}

function IntakeBadge({ intake }) {
  if (!intake) return null;
  const tone =
    intake.status === "submitted" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : intake.status === "expired" ? "bg-zinc-100 text-zinc-600 border-zinc-200"
    : "bg-amber-100 text-amber-800 border-amber-200";
  const label =
    intake.status === "submitted" ? "Form ✓"
    : intake.status === "expired" ? "Form expired"
    : intake.reminder_sent_at ? "Form reminded" : "Form sent";
  return (
    <span
      className={`inline-flex items-center text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone}`}
      title={intake.template_name ? `Template: ${intake.template_name}` : ""}
      data-testid={`intake-badge-${intake.status}`}
    >
      {label}
    </span>
  );
}

function CalcPriceCell({ row }) {
  const meta = row?.calculated_price_meta || {};
  const value = Number(row?.calculated_price || 0);
  const source = meta.source;

  let badge = null;
  let tooltipBody = null;
  let tooltipTitle = "";

  if (source === "amountPaid") {
    badge = <span className="text-2xs text-zinc-500">paid</span>;
    tooltipTitle = "Direct payment";
    tooltipBody = (
      <p>This appointment was paid directly via Acuity (amountPaid).</p>
    );
  } else if (source === "certificate") {
    badge = (
      <span className="text-2xs px-1.5 py-0 h-4 inline-flex items-center rounded border border-emerald-200 bg-emerald-50 text-emerald-700">
        cert
      </span>
    );
    tooltipTitle = meta.product_name || "Certificate";
    tooltipBody = (
      <div className="space-y-1.5">
        <Eq label="Product price" value={`$${(meta.product_total || 0).toFixed(2)}`} />
        <Eq label="Appointments in product" value={meta.appointments_in_product} />
        <Eq
          label="Per-appointment"
          value={`$${value.toFixed(2)}`}
          formula={`$${(meta.product_total || 0).toFixed(2)} ÷ ${meta.appointments_in_product}`}
          highlight
        />
        <p className="pt-1 text-2xs text-zinc-300 font-mono-num">
          cert {meta.certificate}
        </p>
      </div>
    );
  } else if (source === "value_subscription") {
    badge = (
      <span className="text-2xs px-1.5 py-0 h-4 inline-flex items-center rounded border border-sky-200 bg-sky-50 text-sky-700">
        value sub
      </span>
    );
    const v = meta.product_value;
    const tp = meta.appointment_type_price;
    const sessions = meta.appointments_in_product;
    tooltipTitle = meta.product_name || "Value-based subscription";
    tooltipBody = (
      <div className="space-y-1.5">
        <Eq
          label="Sessions per subscription"
          value={sessions}
          formula={`${v} value ÷ $${tp} per class`}
        />
        <Eq label="Subscription price" value={`$${(meta.product_total || 0).toFixed(2)}`} />
        <Eq
          label="Per-appointment"
          value={`$${value.toFixed(2)}`}
          formula={`$${(meta.product_total || 0).toFixed(2)} ÷ ${sessions}`}
          highlight
        />
        <p className="pt-1 text-2xs text-zinc-300 font-mono-num">
          cert {meta.certificate}
        </p>
      </div>
    );
  } else if (source === "value_subscription_unresolved") {
    badge = (
      <span className="text-2xs px-1.5 py-0 h-4 inline-flex items-center rounded border border-amber-200 bg-amber-50 text-amber-700">
        value sub
      </span>
    );
    tooltipTitle = meta.product_name || "Value-based subscription";
    tooltipBody = (
      <p>
        Value-based product, but the appointment-type's published price is missing,
        so the per-class cost cannot be derived.
      </p>
    );
  } else {
    badge = (
      <span className="text-2xs px-1.5 py-0 h-4 inline-flex items-center rounded border border-zinc-200 bg-zinc-50 text-zinc-500">
        unknown
      </span>
    );
    tooltipTitle = "Unknown";
    tooltipBody = meta.certificate ? (
      <p>
        Certificate <span className="font-mono-num">{meta.certificate}</span> could not
        be resolved from the catalog.
      </p>
    ) : (
      <p>No payment and no certificate code on this appointment.</p>
    );
  }

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 cursor-help">
            <span className="font-mono-num text-sm text-zinc-950">
              {value > 0 ? `$${value.toFixed(2)}` : source === "amountPaid" ? "$0.00" : "—"}
            </span>
            {badge}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-sm bg-zinc-950 text-zinc-100 p-3 text-[0.75rem] leading-relaxed"
          data-testid="calc-price-tooltip"
        >
          <div className="font-medium text-white mb-2">{tooltipTitle}</div>
          {tooltipBody}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Eq({ label, value, formula, highlight }) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3",
        highlight && "pt-1 border-t border-zinc-800"
      )}
    >
      <span className="text-zinc-400">{label}</span>
      <span className="text-right">
        {formula && (
          <span className="block text-2xs text-zinc-500 font-mono-num">
            {formula}
          </span>
        )}
        <span
          className={cn(
            "font-mono-num",
            highlight ? "text-white font-medium" : "text-zinc-100"
          )}
        >
          {value}
        </span>
      </span>
    </div>
  );
}

function NotConnected({ resource }) {
  return (
    <Card className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center">
      <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
        Connect Acuity to view {resource}
      </h2>
      <p className="mt-2 text-sm text-zinc-600">
        Add your User ID and API Key in Settings, then return here.
      </p>
      <Link to="/settings">
        <Button className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="goto-settings-btn">
          Open Settings
        </Button>
      </Link>
    </Card>
  );
}
